import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { showAlert } from 'actions/app';
import { CreateMultipleTasksAction, CreationTaskDrawer } from 'features/daily-plan';
import { ORIGIN_TABLE_MAP } from 'features/daily-plan/constants';
import { useTaskDrawerManager } from 'features/daily-plan/hooks';
import {
  CreationSavedSearchDrawer,
  SavedSearchDrawer,
  SearchApplied,
  useSavedSearchDateRestore,
  useSavedSearches
} from 'features/saved-searches';
import { getMatchedStaticDateRange } from 'features/saved-searches/utils';
/** API / EntityRoutes / Endpoints / EntityType */
import useDatatable, {
  addRingCentralContactToStore,
  buildRowActionsRendererDefinition,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import useMultipleSelection, { CustomTableToolbarSelect } from 'hooks/multipleSelection';
import { useSearchProject } from 'hooks/searchProject';
import { useQuickViewPreview } from 'hooks/useQuickViewPreview';
import isEmpty from 'lodash/isEmpty';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { userHasRoles } from 'services/Authorization';
import { When } from 'UI/components/atoms/When';
/** Atoms, Components and Styles */
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
/** Components */
import DataTable from 'UI/components/organisms/DataTable';
import FPQuickView from 'UI/components/organisms/inventoryProfiles/FPQuickView';
import FiltersLayout from 'UI/components/templates/FiltersLayout';
import { additionalPeriodRanges } from 'UI/components/templates/SideFiltersLayout/filters';
/** API / EntityRoutes / Endpoints / EntityType */
import { backNavigateListConfig } from 'UI/constants/config';
import { CommunicationActionPreset, PageTitles } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import {
  createCustomStaticRanges,
  filterColumnsByFeatureFlag,
  getAlertMessageForRequiredFilters
} from 'UI/utils';
import { getMinRequiredFilters } from 'UI/utils/filters';

import {
  ColumnsDefinitions,
  DateFilters,
  FiltersGroups,
  PeriodColumnBySection,
  VirtualProps
} from './columns';

const pageKey = 'companies-list';
const dateTypeFilterKey = 'dateTypeFilter';

const CompaniesColumnsWithFeatureFlag = [
  {
    featureFlag: FeatureFlags.CompaniesHeadquarterColumn,
    columns: ['parentCompany']
  }
];

const MIN_REQUIRED_FILTERS = getMinRequiredFilters();

const CompaniesList = ({ location }) => {
  const dispatch = useDispatch();
  const [currentUser] = useUserContext();
  const isHighLevelUser = userHasRoles(
    [
      Roles.Operations,
      Roles.DataCoordinator,
      Roles.Leadership,
      Roles.AssistantRegionalDirector,
      Roles.Coach
    ],
    currentUser
  );
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const canCreateMultipleTasks = checkIfFeatureFlagEnabled(FeatureFlags.DailyPlanAddTasksFromTable);
  const isSavedSearchesEnabled = checkIfFeatureFlagEnabled(FeatureFlags.CompaniesSavedSearches);
  const isListPersistDateRangeEnabled = checkIfFeatureFlagEnabled(
    FeatureFlags.InventoryListPersistDateRange
  );
  const isSavedSearchesDateEnabled = checkIfFeatureFlagEnabled(FeatureFlags.SavedSearchesDate);

  const {
    activeSearch,
    appliedPeriod,
    buildSavedSearchPayload,
    getRestoredDateTypeFilter,
    getSavedSearchButtonDefinitions,
    handleApplySearch: applySearch,
    handleCloseSavedSearchDrawer,
    handleCloseSavedSearchForm,
    handleDeleteSearch,
    handleEditSavedSearchDrawerOpen,
    handleEditSearch,
    handleRemoveSearch,
    isDirty,
    savedDateTypeId,
    savedSearchesUiState,
    savedSearchRestoreKey,
    shouldUseSavedSearchDates
  } = useSavedSearches({
    defaultDateTypeFilterId: isSavedSearchesDateEnabled ? DateFilters[0].id : null,
    isDateEnabled: isSavedSearchesDateEnabled,
    preferencesKey: pageKey
  });

  const orderByOptions = {
    column: 'name',
    direction: 'asc'
  };

  const filteredColumnsDefinition = filterColumnsByFeatureFlag(
    ColumnsDefinitions,
    CompaniesColumnsWithFeatureFlag
  );

  const initialPreferences = getColumnPreferences(
    pageKey,
    0,
    orderByOptions,
    filteredColumnsDefinition
  );

  const {
    columnPreferences,
    count,
    data,
    filters,
    getData,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersApply,
    handleFiltersChange,
    handleFiltersToggle,
    handleKeywordChange,
    handleOneFilterChange,
    handlePageChange,
    handlePeriodChange: handleDataTablePeriodChange,
    handlePerPageChange,
    handleSectionChange,
    isBelowMinFiltersThreshold,
    activeSection,
    listState,
    mainOptions,
    selectedPeriod,
    queryParams
  } = useDatatable({
    columnsDefinitions: filteredColumnsDefinition,
    enableSavedSearches: isSavedSearchesEnabled && savedSearchesUiState.isSearching,
    endpoint: Endpoints.Companies,
    entityName: 'Companies',
    entityType: EntityType.Company,
    initialPreferences,
    initialSection: isSavedSearchesDateEnabled ? savedDateTypeId : DateFilters[0].id,
    key: pageKey,
    minRequiredFilters: !isHighLevelUser ? MIN_REQUIRED_FILTERS : null,
    orderByOptions,
    periodDefaultValue: shouldUseSavedSearchDates ? appliedPeriod : null,
    periodFilterColumn: PeriodColumnBySection,
    savedSearch: isSavedSearchesEnabled && activeSearch && activeSearch?.search,
    sendDateWithTimezone: true,
    shouldSaveDateRangePreferences: isListPersistDateRangeEnabled && !shouldUseSavedSearchDates,
    shouldScrollOnNavigate: true,
    virtualProps: VirtualProps
  });

  const defaultDateTypeFilter =
    appliedPeriod?.dateTypeFilter ||
    DateFilters.find(item => item.id === activeSection) ||
    DateFilters[0];

  const [dateTypeFilter, setDateTypeFilter] = useState(defaultDateTypeFilter);

  const currentSavedSearchPayload = buildSavedSearchPayload({
    dateTypeFilter,
    filters,
    selectedPeriod
  });

  const restoredDateTypeFilter = getRestoredDateTypeFilter({
    dateFilters: DateFilters,
    fallbackDateFilter: DateFilters[0]
  });

  useEffect(() => {
    document.title = PageTitles.Company;
  }, []);

  useEffect(() => {
    handleEditSearch(
      filters,
      shouldUseSavedSearchDates ? selectedPeriod : null,
      shouldUseSavedSearchDates ? dateTypeFilter?.id : null
    );
  }, [dateTypeFilter, filters, handleEditSearch, selectedPeriod, shouldUseSavedSearchDates]);

  const { resetRestoreKey } = useSavedSearchDateRestore({
    activeSection,
    appliedPeriod,
    dateTypeFilter,
    handleDataTablePeriodChange,
    handleSectionChange,
    isSavedSearchesDateEnabled,
    restoredDateTypeFilter,
    savedSearchRestoreKey,
    selectedPeriod,
    setDateTypeFilter
  });

  const handleApplySearch = useCallback(
    params => {
      resetRestoreKey();
      applySearch(params);
    },
    [applySearch, resetRestoreKey]
  );

  const { columns, columnOrder, orderBy, direction } = columnPreferences;
  const { isLoading, showWholeSkeleton, isSideMenuOpen, page, perPage, keyword, userFilter } =
    listState;

  const dataForSelection = useMemo(
    () => data.map(({ id }) => ({ id, type_id: 6, type: 'Company' })),
    [data]
  );

  const {
    handleRowSelection,
    selectedIndexesInPage,
    multiSelectComponents,
    totalSelected,
    filteredItems
  } = useMultipleSelection({ data: dataForSelection, count, perPage });

  const {
    isDailyPlanTaskDrawerOpen,
    formattedTaskItems,
    allowedTasks,
    tasksQuery,
    handleCloseTaskDrawer,
    handleOpenTaskDrawer,
    handleOnTaskCreated
  } = useTaskDrawerManager({
    table: ORIGIN_TABLE_MAP.Companies,
    selectedItems: filteredItems.data,
    selectionType: filteredItems.type,
    queryParams
  });

  const { SearchProjectAction, SearchProjectForms } = useSearchProject({
    filteredItems,
    queryParams
  });

  const handleAction = ({ rowData }) =>
    addRingCentralContactToStore({
      rowData,
      columns: filteredColumnsDefinition,
      dispatch,
      role: EntityType.Company
    });

  const { entityId, quickViewState, listViewPreset, quickViewEntity, toggleQuickViewPreview } =
    useQuickViewPreview({ columns, data, entityType: EntityType.Company });

  const phoneActionButtons = buildRowActionsRendererDefinition({
    actions: [...CommunicationActionPreset, listViewPreset],
    namespace: EntityType.Company,
    handleAction,
    columns: filteredColumnsDefinition
  });

  const handleDateTypeFilterChange = useCallback(
    (name, value) => {
      setDateTypeFilter(value);
      handleSectionChange && handleSectionChange(name, value.id);
    },
    [handleSectionChange]
  );

  const finalColumns = getColumnsToRender([...columns, phoneActionButtons]);

  const handleTabRefresh = async () => {
    await getData();
  };

  const shouldSelectRequiredFilters = isBelowMinFiltersThreshold && (!data || data.length === 0);
  const savedSearchButtonDefinitions = getSavedSearchButtonDefinitions({ filters });

  const handlePeriodChange = useCallback(
    (newPeriod, isFromStaticPeriod) => {
      const period = newPeriod ? { ...newPeriod, isFromStaticPeriod } : null;
      const matchedStaticDateRange = isFromStaticPeriod ? getMatchedStaticDateRange(period) : null;
      handleDataTablePeriodChange(
        newPeriod ? { ...period, staticDateRangeId: matchedStaticDateRange?.id || null } : null
      );
    },
    [handleDataTablePeriodChange]
  );

  const clearSavedSearchPeriod = () => {
    handleDataTablePeriodChange(null);
    handleDateTypeFilterChange(dateTypeFilterKey, DateFilters[0]);
  };

  const handleSavedSearchRemove = () => {
    const onClearPeriod = isSavedSearchesDateEnabled ? clearSavedSearchPeriod : null;
    handleRemoveSearch({ onClearPeriod });
  };

  const handleSavedSearchDelete = params => {
    const onClearPeriod = isSavedSearchesDateEnabled ? clearSavedSearchPeriod : null;
    handleDeleteSearch({ ...params, onClearPeriod });
  };

  return (
    <FiltersLayout
      title="Companies"
      section={EntityType.Company}
      listSelector={
        <AutocompleteSelect
          name="userFilter"
          placeholder="Companies to show"
          selectedValue={userFilter || mainOptions[0]}
          onSelect={handleOneFilterChange}
          defaultOptions={mainOptions}
          disableClearable
        />
      }
      defaultRange={selectedPeriod || null}
      extraSelector={
        <AutocompleteSelect
          name={dateTypeFilterKey}
          placeholder="Filter By"
          selectedValue={dateTypeFilter}
          onSelect={handleDateTypeFilterChange}
          defaultOptions={DateFilters}
          disableClearable
        />
      }
      dateRangerPickerProps={{
        staticRanges: createCustomStaticRanges(additionalPeriodRanges)
      }}
      filters={filters}
      isSideMenuOpen={isSideMenuOpen}
      isLoading={showWholeSkeleton}
      groups={FiltersGroups}
      enableStore={false}
      onSearch={handleFiltersApply}
      onFiltersChange={handleFiltersChange}
      onMenuToggle={handleFiltersToggle}
      titleLabelProps={backNavigateListConfig}
      onPeriodChange={handlePeriodChange}
      minRequiredFilters={shouldSelectRequiredFilters ? MIN_REQUIRED_FILTERS : null}
      shouldSelectRequiredFilters={shouldSelectRequiredFilters}
      enableSavedSearches={isSavedSearchesEnabled}
      savedSearchesHeader={
        isSavedSearchesEnabled &&
        activeSearch && (
          <SearchApplied
            isDirty={isDirty}
            onRemoveSearch={handleSavedSearchRemove}
            onSaveSearch={!isEmpty(filters) ? handleEditSavedSearchDrawerOpen : null}
            searchName={activeSearch?.name}
            searchOwner={activeSearch?.owner}
          />
        )
      }
      savedSearchesActions={
        isSavedSearchesEnabled && (
          <FPButtonBuilder
            GridContainerProps={{ justify: 'flex-end', spacing: 2 }}
            buttonDefinitions={savedSearchButtonDefinitions}
          />
        )
      }
    >
      <DataTable
        columnOrder={columnOrder?.length ? columnOrder : undefined}
        columns={finalColumns}
        components={multiSelectComponents}
        count={count}
        CustomToolbar={
          <CustomTableToolbarSelect total={totalSelected}>
            <When condition={canCreateMultipleTasks}>
              <CreateMultipleTasksAction onClick={handleOpenTaskDrawer} />
            </When>
            <SearchProjectAction />
          </CustomTableToolbarSelect>
        }
        data={data}
        draggableColumns={{
          enabled: true
        }}
        enableCustomFilters
        filter={false}
        isExpandable
        loading={showWholeSkeleton}
        onColumnDisplayClick={handleColumnDisplayChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnSortChange={handleColumnSortChange}
        onPageClick={handlePageChange}
        onPerPageClick={handlePerPageChange}
        onRowSelectionChange={handleRowSelection}
        onSearchTextChange={handleKeywordChange}
        onToggleFilters={handleFiltersToggle}
        page={page}
        refreshing={isLoading}
        rowsPerPage={perPage}
        rowsSelected={selectedIndexesInPage}
        searchText={keyword}
        selectToolbarPlacement="none"
        sortOrder={{ name: orderBy, direction }}
        subtitle={shouldSelectRequiredFilters ? '' : null}
        title={
          shouldSelectRequiredFilters
            ? getAlertMessageForRequiredFilters(MIN_REQUIRED_FILTERS)
            : null
        }
      />
      <SearchProjectForms />
      <When condition={entityId && quickViewState.isQuickViewOpen}>
        <FPQuickView
          drawerProps={{
            open: quickViewState.isQuickViewOpen
          }}
          onClose={toggleQuickViewPreview}
          entityType={quickViewEntity}
          itemsToNavigate={data}
          id={entityId}
          location={location}
          onEditionCompleted={handleTabRefresh}
        />
      </When>

      <When
        condition={isDailyPlanTaskDrawerOpen && (formattedTaskItems.length > 0 || !!tasksQuery)}
      >
        <CreationTaskDrawer
          allowedTaskTypes={allowedTasks}
          listItems={formattedTaskItems}
          onClose={handleCloseTaskDrawer}
          totalSelected={totalSelected}
          tasksQuery={tasksQuery}
          onTaskCreated={handleOnTaskCreated}
        />
      </When>

      <When condition={isSavedSearchesEnabled && savedSearchesUiState.isSavedSearchDrawerOpen}>
        <SavedSearchDrawer
          activeSearch={activeSearch}
          entityType={EntityType.Company}
          onApplySearch={handleApplySearch}
          onClose={handleCloseSavedSearchDrawer}
          onDeleteSearch={handleSavedSearchDelete}
          open={savedSearchesUiState.isSavedSearchDrawerOpen}
        />
      </When>

      <When
        condition={
          isSavedSearchesEnabled &&
          (savedSearchesUiState.isCreationSavedSearchDrawerOpen ||
            savedSearchesUiState.isEditSavedSearchDrawerOpen)
        }
      >
        <CreationSavedSearchDrawer
          activeSearch={activeSearch}
          entityType={EntityType.Company}
          onApplySearch={handleApplySearch}
          onSaveCompleted={handleCloseSavedSearchForm}
          onClose={handleCloseSavedSearchForm}
          open
          savedSearchId={savedSearchesUiState.isEditSavedSearchDrawerOpen ? activeSearch?.id : null}
          search={currentSavedSearchPayload}
        />
      </When>
    </FiltersLayout>
  );
};

const mapDispatchToProps = dispatch => {
  return {
    onShowAlert: alert => dispatch(showAlert(alert))
  };
};

export default connect(null, mapDispatchToProps)(CompaniesList);
